/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement } from "react"
import { Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { REPORT_UPSTREAM_VERSION } from "../pcg-viewer/upstream-versions"

/**
 * "About Medaica Report" — the prototype's About modal (cardiac_report
 * v0.6.1 #reportAboutModal), reached from the Report brand menu: the version
 * line, whose hover names the absorbed upstream commit, and the prototype's
 * three paragraphs, the last on the interaction diagnostics the Report menu
 * downloads (report-action-log.ts).
 */

const TEXT_SX = { fontSize: 13, lineHeight: 1.45, color: "#334155" }

const HeartReportAboutDialog = ({ open, onClose }: { open: boolean; onClose: () => void }): ReactElement => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        pb: 1,
        fontSize: 16,
        fontWeight: 700,
      }}
    >
      About Medaica Report
      <IconButton aria-label="Close about" size="small" onClick={onClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <Typography component="p" sx={{ ...TEXT_SX, m: "0 0 10px" }}>
        <strong title={REPORT_UPSTREAM_VERSION.source} data-testid="heart-report-about-version">
          Medaica Report v{REPORT_UPSTREAM_VERSION.displayVersion}
        </strong>
      </Typography>
      <Typography component="p" sx={{ ...TEXT_SX, m: "0 0 10px" }}>
        Prepare patient-facing and provider-facing report outputs from evidence reviewed in Medaica Viewer.
      </Typography>
      <Typography component="p" sx={{ ...TEXT_SX, m: "0 0 10px" }}>
        Report is the downstream output workspace; recording suitability and accepted evidence are reviewed in Viewer.
      </Typography>
      <Typography component="p" sx={{ ...TEXT_SX, m: 0 }}>
        First-phase interaction diagnostics are kept in this browser session and can be downloaded from the Report menu.
        Patient-entered text is not recorded.
      </Typography>
    </DialogContent>
  </Dialog>
)

export default HeartReportAboutDialog
